import React, { useState } from 'react';
import { AnimatedSection } from '../components/animated/AnimatedSection';
import { Shield, Eye, Database, Lock, UserCheck, Mail, Phone, ChevronDown, ChevronUp, CheckCircle } from 'lucide-react';
export function PrivacyPolicyPage() {
  const [expandedSection, setExpandedSection] = useState<number | null>(null);
  const [activeSection, setActiveSection] = useState<number | null>(null);
  const toggleSection = (index: number) => {
    setExpandedSection(expandedSection === index ? null : index);
  };
  const timelineData = [{
    icon: Eye,
    color: 'blue',
    title: 'Data Collection',
    subtitle: 'What information we gather',
    content: 'Personal information provided during registration',
    details: ['Personal identifiers: name, email, phone', 'Professional information: job title, organization, industry', 'Payment information: processed via Paystack', 'Usage data: course progress, learning preferences', 'Technical data: IP address, browser type, device', 'Communications: messages, feedback, support requests']
  }, {
    icon: Database,
    color: 'green',
    title: 'Data Processing',
    subtitle: 'How we use your information',
    content: 'Data processed to deliver services and improve experience',
    details: ['Course delivery: provide access and track progress', 'Communication: send updates, certificates, support', 'Payment processing: handle transactions via Paystack', 'Service improvement: analyze usage patterns', 'Legal compliance: meet NDPR requirements', 'Marketing: send recommendations (with consent)']
  }, {
    icon: Database,
    color: 'blue',
    title: 'Data Storage',
    subtitle: 'Where and how long we keep your data',
    content: 'Secure cloud storage with encryption',
    details: ['Storage location: secure cloud servers in compliance regions', 'Encryption: SSL/TLS in transit, AES-256 at rest', 'Retention period: maintained while using services', 'Course data: retained 7 years for certification', 'Marketing data: retained until consent withdrawn', 'Deletion: secure deletion upon request']
  }, {
    icon: UserCheck,
    color: 'green',
    title: 'Your Rights Under NDPR',
    subtitle: 'What you can do with your data',
    content: 'Comprehensive rights under NDPR',
    details: ['Right to access: request copy of all data', 'Right to rectification: correct inaccurate info', 'Right to erasure: request deletion', 'Right to restrict processing: limit data use', 'Right to data portability: receive data in machine-readable format', 'Right to object: opt-out of marketing', 'Right to withdraw consent: revoke anytime', 'Right to lodge complaint: file with NITDA']
  }, {
    icon: Shield,
    color: 'blue',
    title: 'Data Protection',
    subtitle: 'How we keep your information safe',
    content: 'Robust security measures',
    details: ['Technical safeguards: firewalls, intrusion detection, audits', 'Access controls: role-based permissions, MFA', 'Encryption: SSL/TLS and AES-256', 'Regular backups: automated daily with disaster recovery', 'Staff training: mandatory data protection training', 'Third-party vetting: security assessments', 'Incident response: documented breach procedures']
  }];
  const dataRights = ['Request access to your personal data', 'Correct inaccurate information', 'Request deletion of your data', 'Withdraw consent for marketing', 'Export your data in portable format', 'Lodge a complaint with NITDA'];
  return <div className="flex flex-col min-h-screen bg-white" data-id="element-1172">
      <main className="flex-grow pt-20" data-id="element-1173">
        {/* Hero Section */}
        <section className="relative py-32 overflow-hidden" data-id="element-1174">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-green-50" data-id="element-1175" />
          <div className="absolute top-20 right-10 w-96 h-96 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20" data-id="element-1176" />

          <div className="container mx-auto px-6 lg:px-8 relative z-10" data-id="element-1177">
            <div className="max-w-4xl mx-auto text-center" data-id="element-1178">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-blue-600 to-green-600 rounded-full mb-8" data-id="element-1179">
                <Shield className="w-10 h-10 text-white" data-id="element-1180" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mb-6" data-id="element-1181">
                Privacy Policy
              </h1>
              <p className="text-xl text-gray-600 mb-6 leading-relaxed" data-id="element-1182">
                Your privacy matters to us. Learn how we collect, use, and
                protect your personal information.
              </p>
              <p className="text-sm text-gray-500" data-id="element-1183">
                Last updated: January 2025 | Compliant with Nigeria Data
                Protection Regulation (NDPR)
              </p>
            </div>
          </div>
        </section>

        {/* Quick Summary */}
        <AnimatedSection className="py-16 bg-white" data-id="element-1184">
          <div className="container mx-auto px-6 lg:px-8" data-id="element-1185">
            <div className="max-w-6xl mx-auto" data-id="element-1186">
              <div className="bg-gradient-to-br from-blue-50 to-green-50 rounded-3xl p-8 border-2 border-blue-100" data-id="element-1187">
                <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center" data-id="element-1188">
                  Quick Summary
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1189">
                  <div className="bg-white rounded-xl p-6" data-id="element-1190">
                    <Eye className="w-8 h-8 text-blue-600 mb-4" data-id="element-1191" />
                    <h3 className="font-bold text-gray-900 mb-2" data-id="element-1192">
                      What We Collect
                    </h3>
                    <p className="text-sm text-gray-600" data-id="element-1193">
                      Name, email, course data, usage information
                    </p>
                  </div>
                  <div className="bg-white rounded-xl p-6" data-id="element-1194">
                    <Database className="w-8 h-8 text-green-600 mb-4" data-id="element-1195" />
                    <h3 className="font-bold text-gray-900 mb-2" data-id="element-1196">
                      How We Use It
                    </h3>
                    <p className="text-sm text-gray-600" data-id="element-1197">
                      Course delivery, communication, service improvement
                    </p>
                  </div>
                  <div className="bg-white rounded-xl p-6" data-id="element-1198">
                    <UserCheck className="w-8 h-8 text-blue-600 mb-4" data-id="element-1199" />
                    <h3 className="font-bold text-gray-900 mb-2" data-id="element-1200">
                      Your Rights
                    </h3>
                    <p className="text-sm text-gray-600" data-id="element-1201">
                      Access, correct, delete, export data anytime
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </AnimatedSection>

        {/* Visual Data Journey Timeline */}
        <AnimatedSection className="py-24 bg-gradient-to-b from-gray-50 to-white" data-id="element-1202">
          <div className="container mx-auto px-6 lg:px-8" data-id="element-1203">
            <div className="max-w-6xl mx-auto" data-id="element-1204">
              <div className="text-center mb-16" data-id="element-1205">
                <h2 className="text-4xl font-bold text-gray-900 mb-4" data-id="element-1206">
                  Your Data Journey
                </h2>
                <p className="text-xl text-gray-600" data-id="element-1207">
                  Understanding how we handle your information
                </p>
              </div>

              <div className="relative" data-id="element-1208">
                {/* Timeline line */}
                <div className="absolute left-8 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-600 via-green-600 to-blue-600 hidden md:block" data-id="element-1209" />

                <div className="space-y-12" data-id="element-1210">
                  {timelineData.map((item, index) => {
                  const Icon = item.icon;
                  const isExpanded = expandedSection === index;
                  const isActive = activeSection === index;
                  return <div key={index} className={`relative ${index % 2 === 0 ? 'md:pr-12' : 'md:pl-12 md:ml-auto'} md:w-[calc(50%-2rem)]`} onMouseEnter={() => setActiveSection(index)} onMouseLeave={() => setActiveSection(null)} data-id="element-1211">
                        {/* Timeline dot */}
                        <div className={`absolute left-8 top-8 w-4 h-4 rounded-full bg-${item.color}-600 border-4 border-white hidden md:block transform -translate-x-1/2`} data-id="element-1212" />

                        <div className={`bg-white rounded-2xl p-6 border-2 transition-all duration-300 ${isActive ? 'border-blue-500 shadow-xl' : 'border-gray-200 hover:border-blue-300'}`} data-id="element-1213">
                          <div className="flex items-start gap-4 mb-4" data-id="element-1214">
                            <div className={`flex-shrink-0 w-12 h-12 bg-${item.color}-100 rounded-xl flex items-center justify-center`} data-id="element-1215">
                              <Icon className={`w-6 h-6 text-${item.color}-600`} data-id="element-1216" />
                            </div>
                            <div className="flex-1" data-id="element-1217">
                              <h3 className="text-xl font-bold text-gray-900 mb-1" data-id="element-1218">
                                {item.title}
                              </h3>
                              <p className="text-sm text-gray-600 mb-2" data-id="element-1219">
                                {item.subtitle}
                              </p>
                              <p className="text-gray-700" data-id="element-1220">{item.content}</p>
                            </div>
                          </div>

                          <button onClick={() => toggleSection(index)} className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium text-sm transition-colors" data-id="element-1221">
                            {isExpanded ? <>
                                <ChevronUp className="w-4 h-4" data-id="element-1222" />
                                Show less
                              </> : <>
                                <ChevronDown className="w-4 h-4" data-id="element-1223" />
                                Learn more
                              </>}
                          </button>

                          {isExpanded && <div className="mt-4 pt-4 border-t border-gray-200 animate-fadeIn" data-id="element-1224">
                              <ul className="space-y-2" data-id="element-1225">
                                {item.details.map((detail, idx) => <li key={idx} className="flex items-start gap-2 text-sm text-gray-700" data-id="element-1226">
                                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" data-id="element-1227" />
                                    <span data-id="element-1228">{detail}</span>
                                  </li>)}
                              </ul>
                            </div>}
                        </div>
                      </div>;
                })}
                </div>
              </div>
            </div>
          </div>
        </AnimatedSection>

        {/* Your Data Rights Checklist */}
        <AnimatedSection className="py-24 bg-gradient-to-br from-green-50 to-blue-50" data-id="element-1229">
          <div className="container mx-auto px-6 lg:px-8" data-id="element-1230">
            <div className="max-w-4xl mx-auto" data-id="element-1231">
              <div className="text-center mb-12" data-id="element-1232">
                <Lock className="w-12 h-12 text-green-600 mx-auto mb-4" data-id="element-1233" />
                <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-1234">
                  Your Data Rights Checklist
                </h2>
                <p className="text-gray-600" data-id="element-1235">
                  Under NDPR, you have the following rights
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1236">
                {dataRights.map((right, index) => <div key={index} className="bg-white rounded-xl p-4 flex items-start gap-3 shadow-sm hover:shadow-md transition-shadow" data-id="element-1237">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" data-id="element-1238" />
                    <span className="text-gray-700" data-id="element-1239">{right}</span>
                  </div>)}
              </div>
            </div>
          </div>
        </AnimatedSection>

        {/* Contact Section */}
        <AnimatedSection className="py-24 bg-gradient-to-br from-blue-600 to-green-600" data-id="element-1240">
          <div className="container mx-auto px-6 lg:px-8" data-id="element-1241">
            <div className="max-w-4xl mx-auto text-center" data-id="element-1242">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-1243">
                Questions About Your Privacy?
              </h2>
              <p className="text-xl text-blue-100 mb-12" data-id="element-1244">
                Our Data Protection Officer is here to help
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1245">
                <a href="mailto:support@myskillzhq.com" className="bg-white rounded-2xl p-8 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300" data-id="element-1246">
                  <Mail className="w-10 h-10 text-blue-600 mx-auto mb-4" data-id="element-1247" />
                  <h3 className="font-bold text-gray-900 mb-2" data-id="element-1248">Email Us</h3>
                  <p className="text-blue-600" data-id="element-1249">support@myskillzhq.com</p>
                </a>

                <a href="tel:+2348106836707" className="bg-white rounded-2xl p-8 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300" data-id="element-1250">
                  <Phone className="w-10 h-10 text-green-600 mx-auto mb-4" data-id="element-1251" />
                  <h3 className="font-bold text-gray-900 mb-2" data-id="element-1252">Call Us</h3>
                  <p className="text-green-600" data-id="element-1253">0810 683 6707</p>
                </a>
              </div>

              <p className="text-blue-100 mt-8" data-id="element-1254">
                SkillzHQ is committed to protecting your privacy in compliance
                with NDPR
              </p>
            </div>
          </div>
        </AnimatedSection>
      </main>
    </div>;
}